// Notification sound + favicon badge utilities

// localStorage keys for per-user sound preferences. Each user picks
// their own sound + volume; nothing here is shared / server-stored.
const LS_ENABLED = "chat_sound_enabled";
const LS_VOLUME = "chat_sound_volume";
const LS_CUSTOM_URL = "chat_custom_sound_url";   // data: URL when user uploads a file
const LS_CUSTOM_NAME = "chat_custom_sound_name"; // filename for display
// Team-wide default chime — set by an admin via Admin → Sound Settings.
// Cached locally so we don't re-hit the API on every message. The
// admin upload bumps the cache key (we store uploaded_at alongside)
// so devices auto-refresh when the team admin uploads a new file.
const LS_TEAM_URL = "chat_team_default_sound_url";
const LS_TEAM_NAME = "chat_team_default_sound_name";
const LS_TEAM_UPLOADED_AT = "chat_team_default_sound_uploaded_at";

export function getTeamDefaultSoundInfo() {
  try {
    const url = localStorage.getItem(LS_TEAM_URL) || "";
    const name = localStorage.getItem(LS_TEAM_NAME) || "";
    const uploadedAt = localStorage.getItem(LS_TEAM_UPLOADED_AT) || "";
    return url ? { url, name: name || "Team default", uploadedAt } : null;
  } catch { return null; }
}
export function setTeamDefaultSound(info) {
  try {
    if (info && info.url) {
      localStorage.setItem(LS_TEAM_URL, info.url);
      localStorage.setItem(LS_TEAM_NAME, info.name || "Team default");
      if (info.uploadedAt) localStorage.setItem(LS_TEAM_UPLOADED_AT, info.uploadedAt);
    } else {
      localStorage.removeItem(LS_TEAM_URL);
      localStorage.removeItem(LS_TEAM_NAME);
      localStorage.removeItem(LS_TEAM_UPLOADED_AT);
    }
  } catch { /* noop */ }
  notifAudio = null;
}

export function isNotificationSoundEnabled() {
  try { return localStorage.getItem(LS_ENABLED) !== "false"; }
  catch { return true; }
}
export function setNotificationSoundEnabled(enabled) {
  try { localStorage.setItem(LS_ENABLED, enabled ? "true" : "false"); }
  catch { /* noop */ }
}
export function getNotificationVolume() {
  try {
    const v = parseInt(localStorage.getItem(LS_VOLUME) || "", 10);
    if (Number.isFinite(v) && v >= 0 && v <= 100) return v;
  } catch { /* noop */ }
  return 50; // default 50%
}
export function setNotificationVolume(volume) {
  const clamped = Math.max(0, Math.min(100, Math.round(volume)));
  try { localStorage.setItem(LS_VOLUME, String(clamped)); }
  catch { /* noop */ }
  // Invalidate cached audio so the new volume takes effect on next play
  notifAudio = null;
}
export function getCustomSoundInfo() {
  try {
    const url = localStorage.getItem(LS_CUSTOM_URL) || "";
    const name = localStorage.getItem(LS_CUSTOM_NAME) || "";
    return url ? { url, name: name || "Custom sound" } : null;
  } catch { return null; }
}
export function setCustomSound(dataUrl, filename) {
  try {
    if (dataUrl) {
      localStorage.setItem(LS_CUSTOM_URL, dataUrl);
      localStorage.setItem(LS_CUSTOM_NAME, filename || "Custom sound");
    } else {
      localStorage.removeItem(LS_CUSTOM_URL);
      localStorage.removeItem(LS_CUSTOM_NAME);
    }
  } catch { /* noop */ }
  notifAudio = null; // force reload on next play
}

// Generate a short, pleasant chime WAV — soft two-note bell (C5 → G5)
// with sine waves, harmonic overtones, and an exponential decay envelope.
// Sounds like a gentle text-message ping rather than the harsh two-tone
// the previous version used.
function generateNotificationWav() {
  const sampleRate = 44100;
  const duration = 0.55;
  const numSamples = Math.floor(sampleRate * duration);
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, numSamples * 2, true);

  const C5 = 523.25;
  const G5 = 783.99;
  const noteSpacing = 0.10;     // seconds between notes
  const noteDuration = 0.45;    // seconds each note rings

  // Play one bell-like note with a fundamental + soft 2x overtone
  const bell = (t, startT, freq) => {
    const localT = t - startT;
    if (localT < 0 || localT > noteDuration) return 0;
    const env = Math.exp(-localT * 6);            // soft exponential decay
    const fundamental = Math.sin(2 * Math.PI * freq * localT);
    const overtone   = 0.35 * Math.sin(2 * Math.PI * freq * 2 * localT);
    return env * (fundamental + overtone) * 0.45;  // overall amplitude
  };

  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const sample = bell(t, 0, C5) + bell(t, noteSpacing, G5);
    view.setInt16(44 + i * 2, Math.max(-32768, Math.min(32767, Math.floor(sample * 32767))), true);
  }
  return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}

let notifSoundUrl = null;
let notifAudio = null;

export function playNotificationSound() {
  try {
    if (!isNotificationSoundEnabled()) return;
    // Precedence: user-custom → team default → built-in generated chime.
    // Each fallback hands off cleanly if the higher-priority source is
    // missing or its data URL fails to decode.
    const custom = getCustomSoundInfo();
    const team = !custom ? getTeamDefaultSoundInfo() : null;
    const desiredUrl =
      custom?.url ||
      team?.url ||
      (notifSoundUrl ||= generateNotificationWav());
    if (!notifAudio || notifAudio.src !== desiredUrl) {
      notifAudio = new Audio(desiredUrl);
    }
    notifAudio.volume = getNotificationVolume() / 100;
    notifAudio.currentTime = 0;
    notifAudio.play().catch(() => {});
  } catch {
    // Silent fail if audio not available
  }
}

// Unlock audio on first user interaction (required by browser autoplay policy).
// Just setting a flag isn't enough — the browser requires an actual audio
// element to play synchronously inside a user gesture before subsequent
// audio plays will succeed. We play the generated chime at zero volume so
// it's silent but counts as a "user-initiated play" for the autoplay policy.
let audioUnlocked = false;
export function unlockAudio() {
  if (audioUnlocked) return;
  try {
    const url = (notifSoundUrl ||= generateNotificationWav());
    const a = new Audio(url);
    a.volume = 0;          // silent — user shouldn't hear the unlock ping
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        audioUnlocked = true;
        a.pause();
      }).catch(() => {
        // browser declined; we'll try again on the next gesture
      });
    } else {
      audioUnlocked = true;
    }
  } catch {
    // Best-effort: even if the silent-play fails, mark unlocked so we don't
    // re-run on every click; the real chime call will simply fail quietly.
    audioUnlocked = true;
  }
}

// Favicon badge management
let originalFaviconUrl = null;
let currentBadgeState = false;

function getOriginalFavicon() {
  if (originalFaviconUrl) return originalFaviconUrl;
  const link = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
  originalFaviconUrl = link?.href || "/favicon.ico";
  return originalFaviconUrl;
}

export function setFaviconBadge(show) {
  if (show === currentBadgeState) return;
  currentBadgeState = show;

  const faviconUrl = getOriginalFavicon();
  if (!show) {
    // Restore original
    updateFaviconHref(faviconUrl);
    return;
  }

  // Draw favicon with red dot
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const size = 32;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, size, size);
    // Red dot in top-right corner
    const dotRadius = 7;
    ctx.beginPath();
    ctx.arc(size - dotRadius, dotRadius, dotRadius, 0, 2 * Math.PI);
    ctx.fillStyle = "#EF4444";
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    updateFaviconHref(canvas.toDataURL("image/png"));
  };
  img.onerror = () => {
    // If image fails to load, create a simple colored favicon with dot
    const size = 32;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0A2647";
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = "#FFD700";
    ctx.font = "bold 20px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("A", size / 2, size / 2);
    const dotRadius = 7;
    ctx.beginPath();
    ctx.arc(size - dotRadius, dotRadius, dotRadius, 0, 2 * Math.PI);
    ctx.fillStyle = "#EF4444";
    ctx.fill();
    updateFaviconHref(canvas.toDataURL("image/png"));
  };
  img.src = faviconUrl;
}

function updateFaviconHref(href) {
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

// Browser notification (for when tab is not focused)
export function showBrowserNotification(title, body) {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "chat-notification",
      silent: true, // We play our own sound
    });
    setTimeout(() => n.close(), 5000);
  } catch {
    // Silent fail
  }
}
